// src/features/messages/components/MessagesMenu.jsx
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  IconButton,
  List,
  Popover,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Forum as ForumIcon,
  ForumOutlined as ForumOutlinedIcon,
  DoneAll as DoneAllIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../context/AuthContext';
import { useNotificationsContext } from '../../../context/NotificationsContext';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { messagesAPI } from '../services/messages';
import MessageListItem from './MessageListItem';
import MessageThreadDialog from './MessageThreadDialog';
import ComposeMessageDialog from './ComposeMessageDialog';
import UnreadMessagesDialog from './UnreadMessagesDialog';

const PAGE_SIZE = 20;

/**
 * App bar entry for messages: the unread badge, the inbox popover, and the
 * dialogs to read, write and (at login) catch up on messages.
 */
export default function MessagesMenu() {
  const { t } = useTranslation(['messages']);
  const { user } = useAuth();
  const { messageCount, refreshMessageCount } = useNotificationsContext();
  const { showError } = useSnackbar();
  const [anchorEl, setAnchorEl] = useState(null);
  const [filter, setFilter] = useState('all');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [threadId, setThreadId] = useState(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const requestRef = useRef(0);
  const buttonRef = useRef(null);

  const isOpen = Boolean(anchorEl);
  const currentUserId = user?.staff_member_id;
  // The forced password/colour/PIN screens come first; catch up on messages after
  const onboarded = user && !user.force_password_change && user.color && !user.show_pin_setup_prompt;

  const load = useCallback(async ({ offset = 0 } = {}) => {
    const request = ++requestRef.current;
    setLoading(true);
    try {
      const { data } = await messagesAPI.list({
        unread_only: filter === 'unread',
        limit: PAGE_SIZE,
        offset,
      });
      if (request !== requestRef.current) return;
      setItems(prev => (offset ? [...prev, ...data.items] : data.items));
      setTotal(data.total);
    } catch (err) {
      if (request === requestRef.current) showError(err?.response?.data?.message || t('messages:feedback.load_error'));
    } finally {
      if (request === requestRef.current) setLoading(false);
    }
  }, [filter, showError, t]);

  // Fresh list whenever the inbox is opened, the filter changes or a new message arrives
  useEffect(() => {
    if (isOpen) load();
  }, [isOpen, load, messageCount]);

  const handleMarkAllRead = async () => {
    try {
      await messagesAPI.markAllRead();
      refreshMessageCount();
      load();
    } catch (err) {
      showError(err?.response?.data?.message || t('messages:feedback.action_error'));
    }
  };

  const openThread = (message) => {
    setAnchorEl(null);
    setThreadId(message.message_id);
  };

  // Back to the inbox, so catching up on several messages doesn't mean reopening it each time
  const closeThread = () => {
    setThreadId(null);
    setAnchorEl(buttonRef.current);
  };

  const openCompose = () => {
    setAnchorEl(null);
    setComposeOpen(true);
  };

  const hasUnread = items.some(m => !m.is_read || m.unread_comment_count > 0) || messageCount > 0;

  return (
    <>
      <Tooltip title={t('messages:title')}>
        <IconButton
          ref={buttonRef}
          color="inherit"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          aria-label={t('messages:title')}
          aria-haspopup="true"
          aria-expanded={isOpen ? 'true' : undefined}
        >
          <Badge
            badgeContent={messageCount}
            color="error"
            max={99}
            sx={{ '& .MuiBadge-badge': { fontSize: 9, height: 15, minWidth: 15 } }}
          >
            {messageCount > 0 ? <ForumIcon /> : <ForumOutlinedIcon />}
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 420, maxWidth: 'calc(100vw - 16px)', mt: 1, display: 'flex', flexDirection: 'column', maxHeight: 600 } } }}
      >
        <Box sx={{ px: 2, pt: 1.5, pb: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="h6" fontWeight="bold">{t('messages:title')}</Typography>
            <Button variant="contained" size="small" startIcon={<EditIcon />} onClick={openCompose}>
              {t('messages:new_message')}
            </Button>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <ToggleButtonGroup
              exclusive
              size="small"
              aria-label={t('messages:filters.label')}
              value={filter}
              onChange={(_, value) => value && setFilter(value)}
            >
              <ToggleButton value="all" sx={{ px: 1.5, py: 0.25 }}>{t('messages:filters.all')}</ToggleButton>
              <ToggleButton value="unread" sx={{ px: 1.5, py: 0.25 }}>{t('messages:filters.unread')}</ToggleButton>
            </ToggleButtonGroup>
            {hasUnread && (
              <Button size="small" startIcon={<DoneAllIcon />} onClick={handleMarkAllRead}>
                {t('messages:mark_all_read')}
              </Button>
            )}
          </Box>
        </Box>

        <Box sx={{ overflowY: 'auto', flex: 1 }}>
          {items.length === 0 && loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          )}
          {items.length === 0 && !loading && (
            <Box sx={{ textAlign: 'center', px: 3, py: 4 }}>
              <ForumOutlinedIcon sx={{ fontSize: 44, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {filter === 'unread' ? t('messages:empty.unread') : t('messages:empty.all')}
              </Typography>
              {filter === 'all' && (
                <Typography variant="caption" color="text.secondary" component="p">
                  {t('messages:empty.hint')}
                </Typography>
              )}
            </Box>
          )}
          {items.length > 0 && (
            <List disablePadding>
              {items.map(message => (
                <MessageListItem key={message.message_id} message={message} onClick={openThread} />
              ))}
            </List>
          )}
          {items.length > 0 && items.length < total && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
              <Button size="small" onClick={() => load({ offset: items.length })} disabled={loading}>
                {loading ? <CircularProgress size={16} /> : t('messages:load_more')}
              </Button>
            </Box>
          )}
        </Box>
      </Popover>

      {threadId && (
        <MessageThreadDialog messageId={threadId} onClose={closeThread} />
      )}

      <ComposeMessageDialog
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        currentUserId={currentUserId}
      />

      <UnreadMessagesDialog userId={onboarded ? currentUserId : null} />
    </>
  );
}
